<template>
  <div class="hero-area style-three position-relative z-1">
    <!-- Loading State -->
    <div v-if="loading && slides.length === 0" class="hero-loading">
      <div class="spinner-border text-light" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    
    <!-- Swiper Slider -->
    <swiper
      v-else
      :spaceBetween="0"
      :speed="1200"
      :fadeEffect="{ crossFade: true }"
      :effect="'fade'"
      :autoHeight="true"
      :autoplay="{
        delay: 5000,
        disableOnInteraction: false,
      }"
      :pagination="{
        clickable: true,
        dynamicBullets: false,
      }"
      :modules="swiperModules"
      class="hero-slider"
      @slideChange="onSlideChange"
      @swiper="onSwiperReady"
    >
      <swiper-slide v-for="(slide, index) in slides" :key="slide.id || index">
        <div class="hero-slide-item position-relative z-1 overflow-hidden">
          <!-- Video background -->
          <div v-if="slide.isVideo" class="video-container position-absolute top-0 start-0 w-100 h-100">
            <video
              :ref="'slideVideo_' + index"
              :src="slide.backgroundImage"
              class="w-100 h-100 object-fit-cover"
              muted
              playsinline
              preload="auto"
              @ended="onVideoEnded"
            ></video>
          </div>
          <!-- Image background -->
          <div v-else class="img-container position-absolute top-0 start-0 w-100 h-100">
            <img
              :src="slide.backgroundImage"
              :alt="slide.title"
              class="w-100 h-100 object-fit-cover"
            />
          </div>
          <div class="container-fluid position-relative z-1">
            <div class="row">
              <div class="col-lg-7 pe-xxl-5">
                <div class="hero-content">
                  <h2 class="text_secondary fw-bold ls-n2 mb-0">
                    {{ slide.title }}
                  </h2>
                  <h2 v-if="slide.subtitle" class="text-white fw-semibold mb-20 text-uppercase ls-2">
                    {{ slide.subtitle }}
                  </h2>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      </swiper-slide>
    </swiper>
  </div>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { EffectFade, Pagination, Autoplay } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/effect-fade'
import 'swiper/css/pagination'

import { useSiteDefaultsStore } from '@/stores/siteDefaultsStore'

// Import assets
import heroSlide1 from '@/assets/img/hero/hero-slide-1.jpg'
import heroSlide2 from '@/assets/img/hero/hero-slide-2.jpg'
import heroSlide3 from '@/assets/img/hero/hero-slide-3.jpg'
import circleTextSvg from '@/assets/img/hero/circle-text-2.svg'
import favIconSvg from '@/assets/img/icons/fav-icon.svg'

const VIDEO_EXTENSIONS = ['mp4', 'webm', 'ogg', 'mov']

export default {
    name: 'HeroBanner',
    components: {
        Swiper,
        SwiperSlide
    },
    data() {
        return {
            swiperModules: [EffectFade, Pagination, Autoplay],
            swiperInstance: null,
            slides: [],
            statistics: [
                { value: '500', suffix: '+', label: 'Cases Solved' },
                { value: '25', suffix: '+', label: 'Legal Experts' },
                { value: '30', suffix: '+', label: 'Years Experience' }
            ],
            circleTextSvg,
            favIconSvg,
            loading: true
        }
    },
    computed: {
        siteDefaultsStore() {
            return useSiteDefaultsStore()
        }
    },
    async mounted() {
        // Load sliders from CMS
        await this.loadSliders()
        
        // Load statistics from settings
        await this.loadStatistics()
        
        // Initialize GSAP animations
        this.$nextTick(() => {
            this.initAnimations()
            // Auto-play the first video slide if it exists
            this.playActiveVideo()
        })
    },
    methods: {
        /**
         * Check whether a URL points to a video file based on extension.
         */
        isVideoUrl(url) {
            if (!url) return false
            try {
                const pathname = new URL(url, window.location.origin).pathname
                const ext = pathname.split('.').pop()?.toLowerCase()
                return VIDEO_EXTENSIONS.includes(ext)
            } catch {
                return false
            }
        },

        /**
         * Called when Swiper is ready.
         */
        onSwiperReady(swiper) {
            this.swiperInstance = swiper
        },

        /**
         * Called every time Swiper transitions to a new slide.
         * Pauses all videos, then plays the one on the active slide.
         */
        onSlideChange() {
            this.pauseAllVideos()
            this.$nextTick(() => {
                this.playActiveVideo()
            })
        },

        /**
         * Pause every video element in the slider.
         */
        pauseAllVideos() {
            this.slides.forEach((_slide, idx) => {
                const refKey = 'slideVideo_' + idx
                const videoEl = this.$refs[refKey]
                const video = Array.isArray(videoEl) ? videoEl[0] : videoEl
                if (video && video.pause) {
                    video.pause()
                }
            })
        },

        /**
         * Play the video of the currently active slide (if it's a video slide).
         * Pauses Swiper autoplay so the video can play fully;
         * autoplay is resumed when the video ends or on image slides.
         */
        playActiveVideo() {
            if (!this.swiperInstance) return

            const activeIndex = this.swiperInstance.activeIndex
            const activeSlide = this.slides[activeIndex]

            if (!activeSlide || !activeSlide.isVideo) {
                // Image slide – make sure autoplay is running
                if (this.swiperInstance.autoplay && !this.swiperInstance.autoplay.running) {
                    this.swiperInstance.autoplay.start()
                }
                return
            }

            // Video slide – stop autoplay so the video can play in full
            if (this.swiperInstance.autoplay) {
                this.swiperInstance.autoplay.stop()
            }

            const refKey = 'slideVideo_' + activeIndex
            const videoEl = this.$refs[refKey]
            const video = Array.isArray(videoEl) ? videoEl[0] : videoEl

            if (video && video.play) {
                video.currentTime = 0
                video.play().catch(() => {
                    // Autoplay may be blocked – resume Swiper so it doesn't stall
                    if (this.swiperInstance.autoplay) {
                        this.swiperInstance.autoplay.start()
                    }
                })
            }
        },

        /**
         * Fired when a video finishes playing.
         * Advances Swiper to the next slide and resumes autoplay.
         */
        onVideoEnded() {
            if (!this.swiperInstance) return

            // Move to the next slide (loops back to first if at the end)
            if (this.swiperInstance.activeIndex < this.slides.length - 1) {
                this.swiperInstance.slideNext()
            } else {
                this.swiperInstance.slideTo(0)
            }

            // Resume autoplay for subsequent (image) slides
            if (this.swiperInstance.autoplay) {
                this.swiperInstance.autoplay.start()
            }
        },

        async loadSliders() {
            this.loading = true
            
            try {
                const response = await this.$api.get('public/sliders')
                if (response.data && response.data.status === 'success' && response.data.data) {
                    // Map API data to slide structure
                    this.slides = response.data.data.map(slide => ({
                        id: slide.id,
                        title: slide.title || 'Welcome',
                        subtitle: slide.subtitle || '',
                        description: slide.content || slide.subtitle || '',
                        backgroundImage: slide.image || heroSlide1,
                        isVideo: this.isVideoUrl(slide.image)
                    }))
                }
                
                // Fallback to default slides if no data
                if (this.slides.length === 0) {
                    this.slides = [
                        {
                            title: 'PROTECTING YOUR RIGHTS, SECURING YOUR FUTURE',
                            subtitle: 'Expert Legal Services',
                            backgroundImage: heroSlide1,
                            description: 'Expert legal counsel providing strategic solutions for individuals and businesses.',
                            isVideo: false
                        },
                        {
                            title: 'TURN YOUR LEGAL CHALLENGE INTO OPPORTUNITY',
                            subtitle: 'Innovative Solutions',
                            backgroundImage: heroSlide2,
                            description: 'We provide innovative legal solutions designed to protect your assets and secure your future.',
                            isVideo: false
                        },
                        {
                            title: 'COMPREHENSIVE LEGAL EXPERTISE YOU CAN TRUST',
                            subtitle: 'Decades of Experience',
                            backgroundImage: heroSlide3,
                            description: 'With decades of combined experience, we deliver exceptional results across all practice areas.',
                            isVideo: false
                        }
                    ]
                }
            } catch (error) {
                console.error('Failed to load sliders:', error)
                // Use default slides on error
                this.slides = [
                    {
                        title: 'PROTECTING YOUR RIGHTS, SECURING YOUR FUTURE',
                        subtitle: 'Expert Legal Services',
                        backgroundImage: heroSlide1,
                        description: 'Expert legal counsel providing strategic solutions for individuals and businesses.',
                        isVideo: false
                    }
                ]
            } finally {
                this.loading = false
            }
        },
        
        async loadStatistics() {
            try {
                const response = await this.$api.get('public/settings?section=home')
                if (response.data && response.data.status === 'success') {
                    const data = response.data.data
                    
                    // Update statistics if provided
                    if (data.hero_statistics && Array.isArray(data.hero_statistics) && data.hero_statistics.length > 0) {
                        this.statistics = data.hero_statistics
                    }
                }
            } catch (error) {
                console.error('Failed to load statistics:', error)
                // Keep default statistics
            }
        },
        
        initAnimations() {
            // Check if GSAP is available
            if (typeof window.gsap === 'undefined') {
                return
            }
            
            const gsap = window.gsap
            const ScrollTrigger = window.ScrollTrigger
            
            if (!ScrollTrigger) {
                return
            }
            
            // Parallax image animation (only for image containers, not videos)
            const containers = document.querySelectorAll('.hero-area .img-container')
            containers.forEach((container) => {
                const img = container.querySelector('img')
                if (!img) return
                
                gsap.fromTo(
                    img,
                    { yPercent: -20 },
                    {
                        yPercent: 20,
                        ease: 'none',
                        scrollTrigger: { 
                            trigger: container, 
                            scrub: true 
                        },
                    }
                )
            })
        }
    }
}
</script>

<style scoped>
.hero-area.style-three {
    min-height: 100vh;
}

.hero-loading {
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #303E4D 0%, #2C3849 100%);
}

.hero-loading .spinner-border {
    width: 3rem;
    height: 3rem;
}

.hero-slider {
    height: 100vh;
}

.hero-slide-item {
    height: 100vh;
    display: flex;
    align-items: center;
}

.hero-content {
    padding: 100px 0 150px;
}

.hero-content h6 {
    font-size: 16px;
    letter-spacing: 2px;
}

.hero-content h1 {
    font-size: clamp(32px, 5vw, 72px);
    line-height: 1.1;
    margin-bottom: 40px;
}

.circle-text-wrap {
    width: 180px;
    height: 180px;
    margin-top: 40px;
}

.counter-card-wrap {
    gap: 30px;
    margin-bottom: 40px;
}

.counter-card {
    gap: 15px;
}

.counter-card h4 {
    font-size: 48px;
    line-height: 1;
    margin-bottom: 0;
}

.counter-card p {
    font-size: 16px;
    max-width: 120px;
}

.hero-para {
    font-size: 18px;
    line-height: 1.8;
    max-width: 600px;
}

/* Video slide styles */
.video-container {
    background: #000;
}

.video-container video {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

/* Swiper Pagination Dots */
:deep(.swiper-pagination) {
    bottom: 40px !important;
    left: 0 !important;
    right: auto !important;
    width: auto !important;
    padding-left: 12px;
}

:deep(.swiper-pagination-bullet) {
    width: 12px;
    height: 12px;
    background: rgba(255, 255, 255, 0.5);
    opacity: 1;
    margin: 0 8px !important;
    transition: all 0.3s ease;
}

:deep(.swiper-pagination-bullet-active) {
    width: 40px;
    border-radius: 6px;
    background: white;
}

@media (max-width: 1199px) {
    .hero-content {
        padding: 80px 0 120px;
    }
    
    .counter-card h4 {
        font-size: 36px;
    }
    
    .circle-text-wrap {
        width: 150px;
        height: 150px;
    }
}

@media (max-width: 991px) {
    .hero-slider,
    .hero-slide-item {
        height: auto;
        min-height: 100vh;
    }
    
    .hero-content {
        padding: 60px 0 80px;
    }
    
    .counter-card-wrap {
        gap: 20px;
    }
    
    .counter-card h4 {
        font-size: 32px;
    }
}

@media (max-width: 767px) {
    .hero-content h1 {
        font-size: 32px;
    }
    
    .counter-card-wrap {
        flex-direction: column;
        align-items: flex-start !important;
    }
    
    .counter-card {
        width: 100%;
    }
    
    .circle-text-wrap {
        width: 120px;
        height: 120px;
        margin-top: 30px;
    }
    
    :deep(.swiper-pagination) {
        bottom: 20px !important;
    }
}
</style>
